import React, { useState } from 'react';
import { Calendar, CheckCircle, Clock, AlertTriangle, Plus, Loader2 } from 'lucide-react';
import { Modal } from '../../ui/Modal';
import { toast } from 'react-hot-toast';

interface TimelineEvent {
  id: string;
  title: string;
  description: string;
  date: string;
  type: 'milestone' | 'task' | 'meeting' | 'document';
  status: 'completed' | 'in_progress' | 'pending' | 'overdue';
}

interface TimelineProps {
  contractId: string;
}

interface EventForm {
  title: string;
  description: string;
  date: string;
  type: TimelineEvent['type'];
}

const initialFormState: EventForm = {
  title: '',
  description: '',
  date: '',
  type: 'milestone',
};

export function Timeline({ contractId }: TimelineProps) {
  const [events, setEvents] = useState<TimelineEvent[]>([
    {
      id: '1',
      title: 'Project Kickoff',
      description: 'Initial meeting with stakeholders',
      date: '2024-01-15',
      type: 'milestone',
      status: 'completed',
    },
    {
      id: '2',
      title: 'Requirements Document',
      description: 'Technical requirements documentation submitted',
      date: '2024-02-01',
      type: 'document',
      status: 'completed',
    },
    {
      id: '3',
      title: 'Security Assessment',
      description: 'Conduct security assessment of current infrastructure',
      date: '2024-03-15',
      type: 'task',
      status: 'in_progress',
    },
    {
      id: '4',
      title: 'Design Review',
      description: 'Review system design with stakeholders',
      date: '2024-03-20',
      type: 'meeting',
      status: 'pending',
    },
  ]);

  const [showAddEventModal, setShowAddEventModal] = useState(false);
  const [form, setForm] = useState<EventForm>(initialFormState);
  const [saving, setSaving] = useState(false);

  const handleAddEvent = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);

    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));

      const newEvent: TimelineEvent = {
        id: crypto.randomUUID(),
        ...form,
        status: 'pending',
      };

      setEvents(prev => [...prev, newEvent]);
      setShowAddEventModal(false);
      setForm(initialFormState);
      toast.success('Event added successfully');
    } catch (error) {
      toast.error('Failed to add event');
      console.error('Failed to add event:', error);
    } finally {
      setSaving(false);
    }
  };

  const getStatusIcon = (status: TimelineEvent['status']) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="h-5 w-5 text-green-500" />;
      case 'in_progress':
        return <Clock className="h-5 w-5 text-yellow-500" />;
      case 'pending':
        return <Clock className="h-5 w-5 text-gray-400" />;
      case 'overdue':
        return <AlertTriangle className="h-5 w-5 text-red-500" />;
    }
  };

  const getTypeColor = (type: TimelineEvent['type']) => {
    switch (type) {
      case 'milestone':
        return 'bg-purple-100 text-purple-800';
      case 'task':
        return 'bg-blue-100 text-blue-800';
      case 'meeting':
        return 'bg-green-100 text-green-800';
      case 'document':
        return 'bg-orange-100 text-orange-800';
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-medium text-gray-900">Project Timeline</h3>
        <button
          onClick={() => setShowAddEventModal(true)}
          className="inline-flex items-center rounded-md bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
        >
          <Plus className="mr-2 h-4 w-4" />
          Add Event
        </button>
      </div>

      <div className="flow-root">
        <ul className="-mb-8">
          {events.map((event, eventIdx) => (
            <li key={event.id}>
              <div className="relative pb-8">
                {eventIdx !== events.length - 1 ? (
                  <span
                    className="absolute left-4 top-4 -ml-px h-full w-0.5 bg-gray-200"
                    aria-hidden="true"
                  />
                ) : null}
                <div className="relative flex space-x-3">
                  <div>
                    <span
                      className={`flex h-8 w-8 items-center justify-center rounded-full ring-8 ring-white ${
                        event.status === 'completed'
                          ? 'bg-green-500'
                          : event.status === 'in_progress'
                          ? 'bg-yellow-500'
                          : event.status === 'overdue'
                          ? 'bg-red-500'
                          : 'bg-gray-400'
                      }`}
                    >
                      {getStatusIcon(event.status)}
                    </span>
                  </div>
                  <div className="flex min-w-0 flex-1 justify-between space-x-4 pt-1.5">
                    <div>
                      <div className="flex items-center space-x-2">
                        <p className="text-sm font-medium text-gray-900">{event.title}</p>
                        <span
                          className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-medium ${getTypeColor(
                            event.type
                          )}`}
                        >
                          {event.type}
                        </span>
                      </div>
                      <p className="mt-0.5 text-sm text-gray-500">{event.description}</p>
                    </div>
                    <div className="whitespace-nowrap text-right text-sm text-gray-500">
                      <time dateTime={event.date}>
                        {new Date(event.date).toLocaleDateString()}
                      </time>
                    </div>
                  </div>
                </div>
              </div>
            </li>
          ))}
        </ul>
      </div>

      {/* Add Event Modal */}
      <Modal
        isOpen={showAddEventModal}
        onClose={() => setShowAddEventModal(false)}
        title="Add Timeline Event"
      >
        <form onSubmit={handleAddEvent} className="space-y-4">
          <div>
            <label htmlFor="title" className="block text-sm font-medium text-gray-700">
              Event Title
            </label>
            <input
              type="text"
              id="title"
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
              required
            />
          </div>

          <div>
            <label htmlFor="description" className="block text-sm font-medium text-gray-700">
              Description
            </label>
            <textarea
              id="description"
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              rows={3}
              className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
              required
            />
          </div>

          <div>
            <label htmlFor="date" className="block text-sm font-medium text-gray-700">
              Date
            </label>
            <input
              type="date"
              id="date"
              value={form.date}
              onChange={(e) => setForm({ ...form, date: e.target.value })}
              className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
              required
            />
          </div>

          <div>
            <label htmlFor="type" className="block text-sm font-medium text-gray-700">
              Event Type
            </label>
            <select
              id="type"
              value={form.type}
              onChange={(e) => setForm({ ...form, type: e.target.value as TimelineEvent['type'] })}
              className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
              required
            >
              <option value="milestone">Milestone</option>
              <option value="task">Task</option>
              <option value="meeting">Meeting</option>
              <option value="document">Document</option>
            </select>
          </div>

          <div className="flex justify-end space-x-3">
            <button
              type="button"
              onClick={() => setShowAddEventModal(false)}
              className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="inline-flex items-center rounded-md bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
            >
              {saving ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Adding...
                </>
              ) : (
                <>
                  <Plus className="mr-2 h-4 w-4" />
                  Add Event
                </>
              )}
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
}